import { Fragment } from "react";
import { cn } from "@/shared/lib/cn";
import "./shopHero.css";

export interface ShopHeroSegment {
  /** El texto ya traducido. Se parte por espacios. */
  text: string;
  /** `true` = va con el degradado de la marca. */
  accent?: boolean;
}

export interface ShopHeroTitleProps {
  segments: readonly ShopHeroSegment[];
  animate: boolean;
  /** Retardo de la PRIMERA palabra, en ms. */
  startDelay?: number;
  /** Cuánto espera cada palabra respecto de la anterior, en ms. */
  step?: number;
  className?: string;
}

/**
 * El titular, palabra a palabra.
 *
 * ── El deslizamiento con máscara ─────────────────────────────────────
 * Cada palabra va dentro de un recorte de su propio alto y entra subiendo
 * desde debajo. Es el mismo gesto de `letters-slide-up` de la landing y
 * el que pidió Anahi como referencia de comportamiento; lo que aporta
 * frente a un simple fundido es que la palabra parece venir de algún
 * sitio en vez de materializarse.
 *
 * ── Se parte por palabras y no por letras ────────────────────────────
 * Con letras sueltas, un lector de pantalla puede leer «B, i, e, n…» —
 * cada `<span>` es un nodo de texto independiente y algunos motores los
 * anuncian por separado. Por palabras el texto sigue siendo texto, se
 * puede seleccionar y copiar entero, y el escalonado se sigue notando.
 *
 * ── El índice de la palabra atraviesa los segmentos ──────────────────
 * Los segmentos existen solo para saber cuál lleva degradado; el retardo
 * se calcula sobre la lista aplanada, así que la ola no se reinicia al
 * empezar el trozo resaltado. Se aplana ANTES de pintar por eso mismo.
 *
 * ── El espacio entre palabras es un nodo de texto de verdad ──────────
 * Y no un margen: `overflow: hidden` convierte cada palabra en un
 * `inline-block`, y JSX se come el espacio en blanco entre elementos
 * hermanos. Sin el `{" "}` explícito el titular sale todojunto — y con
 * márgenes, el texto centrado del móvil queda descuadrado por el margen
 * sobrante de la última palabra.
 */
export function ShopHeroTitle({
  segments,
  animate,
  startDelay = 0,
  step = 70,
  className,
}: ShopHeroTitleProps) {
  const words = segments.flatMap((segment) =>
    segment.text
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => ({ word, accent: segment.accent ?? false })),
  );

  return (
    <h1 className={className}>
      {words.map(({ word, accent }, index) => (
        <Fragment key={`${index}-${word}`}>
          {index > 0 ? " " : null}
          <span className="shop-hero-word">
            <span
              className={cn(
                /*
                 * El degradado va en VERTICAL a propósito. En diagonal,
                 * cada palabra recorre el degradado entero a lo largo de
                 * su propio ancho: "siguiente" salía gris y "nivel."
                 * blanca, y el titular parecía mal renderizado en vez de
                 * resaltado. En vertical todas las palabras reciben
                 * exactamente el mismo tratamiento —blanco arriba, lila en
                 * la base— midan lo que midan.
                 *
                 * No se puede tirar el degradado a lo ancho de la frase
                 * entera: cada palabra es su propio recorte
                 * (`overflow: hidden`) para el deslizamiento, y eso corta
                 * cualquier fondo heredado.
                 */
                accent &&
                  "bg-gradient-to-b from-white to-ds-lilac bg-clip-text font-semibold text-transparent",
                animate && "shop-hero-rise",
              )}
              style={animate ? { animationDelay: `${startDelay + step * index}ms` } : undefined}
            >
              {word}
            </span>
          </span>
        </Fragment>
      ))}
    </h1>
  );
}
